import * as React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import {
  useGetFieldAdminHSDayQuery,
  useStartFieldAdminHSDayMutation,
  useGetFieldAdminQEOversightQuery,
  useGetFieldAdminStockBalanceQuery,
  useGetFieldAdminSitesQuery,
  useGetFieldAdminTeamOverviewQuery,
  useGetFieldAdminVehiclesQuery,
} from "./fieldAdminApi";
import { getUserName } from "../Authentication/authSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FAPageState } from "./FAStates";
import { OK_GREEN_TEXT, WARN_YELLOW, BAD_RED_TEXT, parseLocalDate } from "./designTokens";
import { computeStepMeta, WorkflowTileList } from "./HS/WorkflowSummary";
import { CollectIcon, DistributeIcon, ReturnsIcon } from "./icons";
import { FAPageShell, FAStatTile, RecordRow } from "./FALayout";
import { DayCompleteCard, TeamStrip } from "./HomePanels";

export default function Home() {
  const navigate = useNavigate();
  const userName = useSelector(getUserName);

  const { data: day, isSuccess: isDaySuccess, isError: isDayError, error: dayError, refetch: refetchDay } = useGetFieldAdminHSDayQuery();
  const [startDay, { isLoading: isStarting }] = useStartFieldAdminHSDayMutation();
  const { data: qe, isSuccess: isQeSuccess, isError: isQeError, error: qeError } = useGetFieldAdminQEOversightQuery();
  const { data: stock, isSuccess: isStockSuccess, isError: isStockError, error: stockError } = useGetFieldAdminStockBalanceQuery();
  const { data: sitesData, isSuccess: isSitesSuccess, isError: isSitesError, error: sitesError } = useGetFieldAdminSitesQuery();
  // The team strip's own data. Not gated on below: the dashboard is still useful if the
  // IMS-backed overview is slow or unavailable, so the strip just hides itself.
  const { data: overview, isSuccess: isOverviewSuccess } = useGetFieldAdminTeamOverviewQuery();
  const { data: fleet, isSuccess: isFleetSuccess } = useGetFieldAdminVehiclesQuery();

  // One failing endpoint used to leave the whole dashboard permanently blank.
  if (!isDaySuccess || !isQeSuccess || !isStockSuccess || !isSitesSuccess) {
    const isError = isDayError || isQeError || isStockError || isSitesError;
    // Whichever query actually failed — they all 403 together when the field admin has
    // no team, so the first defined one carries the message that matters.
    const error = dayError || qeError || stockError || sitesError;
    return <FAPageState isError={isError} error={error} onRetry={refetchDay} count={5} tiles={4} />;
  }

  const sites = sitesData.sites;

  const assignedVehicle = isFleetSuccess
    ? fleet.vehicles.find((v) => v.assigned_team_id === fleet.team_id)
    : null;

  const steps = computeStepMeta(day);
  const completedCount = steps.filter((s) => s.done).length;

  const approvedSites = sites.filter((s) => s.status === "approved").length;
  const pendingSites = sites.filter((s) => s.status === "pending").length;
  const declinedSites = sites.filter((s) => s.status === "declined").length;

  const cocsTotal = qe.cocs_issued_today + qe.awaiting_count;
  // Honest "expected still to collect" — both sides are real (admin-set allocation,
  // today's collected count); omitted entirely if nobody's configured an allocation.
  const scanToGo = stock.allocated != null ? Math.max(stock.allocated - stock.collected, 0) : null;

  return (
      <FAPageShell>
        {/* index.html .top-row — greeting and team strip share a two-column top row. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) minmax(0,1.4fr)" }, gap: "18px", alignItems: "stretch", mb: "18px" }}>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", mb: "4px" }}>
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </Typography>
            <Typography sx={{ fontSize: "1.85rem", fontWeight: 700, letterSpacing: "-0.025em", color: "#fff" }}>
              {userName || "Field Admin"}
            </Typography>
            <Typography sx={{ fontSize: "0.92rem", color: "rgba(255,255,255,0.55)", mt: "4px" }}>
              FA
              {isOverviewSuccess && overview.team_name ? (
                <> · <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{overview.team_name}</Box></>
              ) : null}
              {isOverviewSuccess && overview.company_name ? ` · ${overview.company_name}` : ""}
            </Typography>
          </Box>

          {isOverviewSuccess ? (
            <TeamStrip overview={overview} vehicle={assignedVehicle} onNavigate={navigate} />
          ) : null}
        </Box>

        {/* Day status — the design's three states, now that DayWorkflow carries one.
            Keyed on `locked`, never on completed_at: a reopened day still has a sign-off
            time (it is the trail), so testing completed_at would leave a reopened day
            showing as complete and hide the workflow the FA just reopened to finish. */}
        {day.locked ? (
          <DayCompleteCard day={day} qe={qe} completedCount={completedCount} onView={() => navigate("/field-admin/hs/")} />
        ) : day.status === "not_started" ? (
          <DayStartCard
            day={day}
            isStarting={isStarting}
            onStart={async () => {
              await startDay().unwrap();
              navigate("/field-admin/hs/");
            }}
          />
        ) : (
        // Both terminal states are handled above, so this arm is the in-progress day.
        <Box
          onClick={() => navigate("/field-admin/hs/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            backgroundColor: "rgba(253,185,64,0.08)",
            border: "1px solid rgba(253,185,64,0.28)",
            borderRadius: "14px",
            px: "18px",
            py: "16px",
            mb: "22px",
            cursor: "pointer",
          }}
        >
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(253,185,64,0.14)", color: AMBER, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: "1.3rem" }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>
              Continue today's workflow
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>
              {`${completedCount} of 5 H&S checks complete today`}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: AMBER,
              color: "#14100a",
              fontWeight: 700,
              fontSize: "0.8rem",
              borderRadius: "8px",
              px: "14px",
              py: "9px",
            }}
          >
            Continue →
          </Box>
        </Box>
        )}

        {/* Quick stats — real counts only, no install forecast/target figures (blocked
            on the Team model). Stock on van's "/allocated" denominator is real, though —
            admin-set (TeamStockAllocation), not blocked on Team, just not IMS-synced yet;
            falls back to a bare count for any team nobody's configured one for. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "10px", mb: "24px" }}>
          <FAStatTile
            label="Attendance"
            value={<>{day.attendance_verified}<Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.62em" }}> / {day.attendance_total}</Box></>}
            sub={day.attendance_total === 0 ? "Not started yet" : day.attendance_verified === day.attendance_total ? "All verified" : `${day.attendance_total - day.attendance_verified} not verified`}
            subColor={day.attendance_total === 0 ? undefined : day.attendance_verified === day.attendance_total ? OK_GREEN_TEXT : WARN_YELLOW}
            info="Per-installer sign-in/out for today, verified by the field admin."
          />
          <FAStatTile label="Installs today" value={qe.installs_done_today} sub="Completed today" />
          <FAStatTile
            label="COCs (QE)"
            value={<>{qe.cocs_issued_today}<Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.62em" }}> / {cocsTotal}</Box></>}
            sub={qe.awaiting_count === 0 ? "All caught up" : qe.awaiting_count <= 1 ? "QE keeping pace" : `${qe.awaiting_count} awaiting`}
            subColor={qe.awaiting_count > 1 ? AMBER : OK_GREEN_TEXT}
            info="Read-only feed of COC-completion progress from the QE app."
          />
          <FAStatTile
            label="Stock on van"
            value={stock.allocated != null ? <>{stock.on_van}<Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.62em" }}> / {stock.allocated}</Box></> : stock.on_van}
            sub={stock.returned > 0 ? `${stock.returned} return${stock.returned === 1 ? "" : "s"} flagged` : `${stock.collected} collected today`}
            info="Devices collected today, minus what's been issued to installers."
          />
        </Box>

        {/* Today's workflow — identical to the H&S hub's own tile list. */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Today's workflow
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>{completedCount} of {steps.length} complete</Typography>
        </Box>
        <Box sx={{ mb: "24px" }}>
          <WorkflowTileList steps={steps} columns={2} />
        </Box>

        {/* Field ops */}
        <Box sx={{ py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Field ops
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mb: "24px" }}>
          <OpsTile
            Icon={PlaceOutlinedIcon}
            iconBg="rgba(253,185,64,0.12)"
            iconColor={AMBER}
            name="Site pipeline"
            value={sites.length}
            meta={[
              { text: `${approvedSites} approved`, color: OK_GREEN_TEXT },
              { text: `${pendingSites} pending`, color: WARN_YELLOW },
              { text: `${declinedSites} declined`, color: BAD_RED_TEXT },
            ]}
            info="Sites with Approved / Pending / Declined statuses. Forecasted vs actual install count tracked per site."
            onClick={() => navigate("/field-admin/sites/")}
          />
          <OpsTile
            Icon={HowToRegOutlinedIcon}
            iconBg="rgba(168,85,247,0.12)"
            iconColor="#C4B5FD"
            name="QE oversight"
            value={<>{qe.cocs_issued_today}<Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.62em" }}> / {cocsTotal}</Box></>}
            meta={
              qe.awaiting_count <= 1
                ? [{ text: "On track", color: OK_GREEN_TEXT }, { text: `${qe.awaiting_count} awaiting COC`, color: undefined }]
                : [{ text: `${qe.awaiting_count} awaiting COC`, color: undefined }]
            }
            info="Read-only QE COC-completion feed. FA cannot approve or decline — only prompt the QE if a backlog forms."
            onClick={() => navigate("/field-admin/more/qe-oversight/")}
          />
        </Box>

        {/* Stock */}
        <Box sx={{ py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Stock
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "24px" }}>
          <StockRow
            Icon={CollectIcon}
            iconBg="rgba(253,185,64,0.12)"
            iconColor={AMBER}
            name="Scan to collect"
            sub="Log devices received from the depot onto the van"
            statusLabel={scanToGo != null ? `${scanToGo} to scan` : null}
            tone="progress"
            onClick={() => navigate("/field-admin/stock/collect/")}
          />
          <StockRow
            Icon={DistributeIcon}
            iconBg="rgba(255,255,255,0.05)"
            iconColor="rgba(255,255,255,0.65)"
            name="Distribute to installers"
            sub="Assign devices to installers · tracked against returns"
            statusLabel={`${stock.issued} issued`}
            tone="progress"
            onClick={() => navigate("/field-admin/stock/issue/")}
          />
          <StockRow
            Icon={ReturnsIcon}
            iconBg="rgba(239,68,68,0.12)"
            iconColor="#F87171"
            name="Returns & uninstallable"
            sub="Faulty, damaged, refused — reason recorded"
            statusLabel={`${stock.returned} flagged`}
            tone="alert"
            onClick={() => navigate("/field-admin/stock/return/")}
          />
        </Box>

        {/* Records & audit */}
        <Box sx={{ py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Records & audit
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          <RecordRow
            Icon={DescriptionOutlinedIcon}
            name="H&S file · PDF export"
            info="Date-range PDF for the physical Health & Safety file. Monthly export per FA."
            sub="Date-range · monthly · DoL-ready"
            cta="Export →"
            onClick={() => navigate("/field-admin/hs/export/")}
            isLast
          />
        </Box>
      </FAPageShell>
  );
}

// The day's opening state, shown before anything has been recorded. Pressing Start is not
// the only way in — the first write to any H&S page opens the day too (see
// get_workflow_for_write) — so this is an affordance, not a gate.
function DayStartCard({ day, isStarting, onStart }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: "14px",
        backgroundColor: "rgba(255,255,255,0.03)",
        border: `1px solid ${BORDER}`,
        borderRadius: "14px", px: "18px", py: "16px", mb: "22px",
      }}
    >
      <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(253,185,64,0.14)", color: AMBER, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <PlayArrowRoundedIcon sx={{ fontSize: "1.5rem" }} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>Start today&rsquo;s workflow</Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>
          {`Nothing recorded yet · ${parseLocalDate(day.date).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}`}
        </Typography>
      </Box>
      <Box
        component="button"
        type="button"
        disabled={isStarting}
        onClick={onStart}
        sx={{
          flexShrink: 0, border: "none", cursor: isStarting ? "default" : "pointer",
          fontFamily: "inherit", backgroundColor: AMBER, color: "#14100a",
          fontWeight: 700, fontSize: "0.8rem", borderRadius: "8px", px: "14px", py: "9px",
          opacity: isStarting ? 0.6 : 1,
        }}
      >
        {isStarting ? "Starting…" : "Start day →"}
      </Box>
    </Box>
  );
}

// index.html .day-complete-card — the day's terminus, shown in place of the continue banner.
function OpsTile({ Icon, iconBg, iconColor, name, value, meta, info, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{ position: "relative", backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "14px", display: "flex", flexDirection: "column", gap: "10px", cursor: "pointer" }}
    >
      <Box sx={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: "1.05rem" }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "#fff" }}>{name}</Typography>
        <Typography sx={{ fontSize: "1.45rem", fontWeight: 700, letterSpacing: "-0.02em", color: "#fff", mt: "6px" }}>{value}</Typography>
      </Box>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
        {meta.map((segment, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <Box component="span" sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.4)", flexShrink: 0 }} /> : null}
            <Box component="span" sx={{ color: segment.color || "inherit" }}>{segment.text}</Box>
          </React.Fragment>
        ))}
      </Typography>
      {info ? (
        <Tooltip title={info} arrow placement="top">
          <InfoOutlinedIcon sx={{ position: "absolute", bottom: "8px", right: "8px", fontSize: "0.85rem", color: "rgba(255,255,255,0.25)", cursor: "help" }} />
        </Tooltip>
      ) : null}
    </Box>
  );
}

// Full-width Stock row — same shape as the H&S WorkflowTileList row (icon · title+pill ·
// description · chevron), just without a progress bar; kept separate from
// WorkflowTileList since it's a different semantic domain (a summary link, not a
// day-workflow step), and no left-border accent per the source (that's H&S-only).
const STOCK_TONE_STYLES = {
  progress: { bg: "rgba(253,185,64,0.14)", color: AMBER },
  alert: { bg: "rgba(239,68,68,0.12)", color: BAD_RED_TEXT },
};
function StockRow({ Icon, iconBg, iconColor, name, sub, statusLabel, tone, onClick }) {
  const toneStyle = STOCK_TONE_STYLES[tone] || STOCK_TONE_STYLES.progress;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: "14px", px: "16px", py: "14px", cursor: "pointer",
        backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px",
        transition: "border-color 0.2s, transform 0.15s",
        "&:hover": { borderColor: "rgba(253,185,64,0.35)", transform: "translateY(-1px)" },
      }}
    >
      <Box sx={{ width: "38px", height: "38px", borderRadius: "9px", backgroundColor: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: "1.2rem" }} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
          <Typography sx={{ color: "#fff", fontSize: "0.9rem", fontWeight: 600 }}>{name}</Typography>
          {statusLabel ? (
            <Box sx={{ fontSize: "0.66rem", fontWeight: 700, px: "8px", py: "2px", borderRadius: "4px", letterSpacing: "0.03em", textTransform: "uppercase", flexShrink: 0, backgroundColor: toneStyle.bg, color: toneStyle.color }}>
              {statusLabel}
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem", mt: "2px" }}>{sub}</Typography>
      </Box>
      <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: "1.1rem", flexShrink: 0 }} />
    </Box>
  );
}
